import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminLayout from "../../components/layouts/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Store, Users, ShoppingCart, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    total_restaurants: 0,
    active_restaurants: 0,
    total_orders: 0,
    total_users: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Restaurants",
      value: stats.total_restaurants,
      icon: Store,
      color: "bg-blue-500",
      bgColor: "bg-blue-50",
    },
    {
      title: "Active Restaurants",
      value: stats.active_restaurants,
      icon: TrendingUp,
      color: "bg-green-500",
      bgColor: "bg-green-50",
    },
    {
      title: "Total Orders",
      value: stats.total_orders,
      icon: ShoppingCart,
      color: "bg-orange-500",
      bgColor: "bg-orange-50",
    },
    {
      title: "Total Users",
      value: stats.total_users,
      icon: Users,
      color: "bg-purple-500",
      bgColor: "bg-purple-50",
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Platform Overview</h1>
          <p className="text-muted-foreground">Monitor your restaurant platform</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <Card key={index} className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                    <stat.icon className={`w-6 h-6 ${stat.color.replace("bg-", "text-")}`} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                    <p className="text-2xl font-bold">
                      {loading ? "..." : stat.value.toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a
                href="/admin/restaurants"
                className="p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors text-center"
                data-testid="quick-action-restaurants"
              >
                <Store className="w-8 h-8 mx-auto mb-2 text-primary" />
                <p className="font-medium">Manage Restaurants</p>
                <p className="text-sm text-muted-foreground">Add or edit restaurants</p>
              </a>
              <a
                href="/admin/users"
                className="p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors text-center"
                data-testid="quick-action-users"
              >
                <Users className="w-8 h-8 mx-auto mb-2 text-primary" />
                <p className="font-medium">View Users</p>
                <p className="text-sm text-muted-foreground">Manage user access</p>
              </a>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 text-green-600" />
                <p className="font-medium text-green-900">Platform Health</p>
                <p className="text-sm text-green-700">All systems operational</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
